import React, { useState, useCallback, useEffect } from 'react'
import type { Content, ContentDetail, ContentStatus, Collection } from './types'
import ContentCard from './ContentCard'
import ContentDetailModal from './ContentDetailModal'
import DeleteConfirmModal from './DeleteConfirmModal'
import ContentUploadPanel from '../ContentUploadPanel'
import { useWordPressImport, WordPressImportDrawer } from './wordpress'
import { useAppLanguage } from '../../i18n'

type ContentsPanelProps = {
  contents: Content[]
  collections: Collection[]
  loading: boolean
  panelTitle: string
  selectedCollectionId: number | 'orphan' | null
  workingIds: Record<number, string>
  statusFilter: ContentStatus | 'all'
  onStatusFilterChange: (status: ContentStatus | 'all') => void
  onReload: () => Promise<void>
  onRemove: (id: number) => Promise<boolean>
  onReindex: (id: number, strategy?: string) => Promise<boolean>
  onForceTranscribe: (id: number) => Promise<boolean>
  onGetDetail: (id: number) => Promise<ContentDetail | null>
  onDownloadTranscription: (id: number) => Promise<void>
  onUpdateCollection: (id: number, collectionId: number | null) => Promise<boolean>
  uploadOpen?: boolean
  onUploadOpenChange?: (open: boolean) => void
}

/**
 * Panel principal des contenus
 * Responsabilité unique : liste et gestion des contenus
 */
export default function ContentsPanel({
  contents,
  collections,
  loading,
  panelTitle,
  selectedCollectionId,
  workingIds,
  statusFilter,
  onStatusFilterChange,
  onReload,
  onRemove,
  onReindex,
  onForceTranscribe,
  onGetDetail,
  onDownloadTranscription,
  onUpdateCollection,
  uploadOpen: uploadOpenProp,
  onUploadOpenChange,
}: ContentsPanelProps) {
  const { t } = useAppLanguage()
  const [uploadOpenInternal, setUploadOpenInternal] = useState(false)
  const uploadOpen = uploadOpenProp ?? uploadOpenInternal
  const setUploadOpen = (open: boolean) => {
    setUploadOpenInternal(open)
    onUploadOpenChange?.(open)
  }
  const [detailContentId, setDetailContentId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Content | null>(null)
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 15

  // Remettre à la page 1 quand le filtre ou la collection change
  useEffect(() => { setPage(1) }, [statusFilter, contents.length])

  // WordPress Import - pré-sélectionner la collection si une est choisie
  const wpImport = useWordPressImport({
    onImportComplete: () => onReload(),
    initialCollectionId: typeof selectedCollectionId === 'number' ? selectedCollectionId : null,
  })

  const handleUploadComplete = useCallback(() => {
    setUploadOpen(false)
    onReload()
  }, [onReload])

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return false
    const success = await onRemove(deleteTarget.id)
    return success
  }, [deleteTarget, onRemove])

  const handleReindex = useCallback(async (id: number) => {
    await onReindex(id)
    // Refresh detail if open
    if (detailContentId === id) {
      // Force refresh by closing and reopening
      setDetailContentId(null)
      setTimeout(() => setDetailContentId(id), 100)
    }
  }, [onReindex, detailContentId])

  const handleLLMSegment = useCallback(async (id: number) => {
    await onReindex(id, 'llm_segmentation')
    if (detailContentId === id) {
      setDetailContentId(null)
      setTimeout(() => setDetailContentId(id), 100)
    }
  }, [onReindex, detailContentId])

  const handleForceTranscribe = useCallback(async (id: number) => {
    await onForceTranscribe(id)
    if (detailContentId === id) {
      setDetailContentId(null)
      setTimeout(() => setDetailContentId(id), 100)
    }
  }, [onForceTranscribe, detailContentId])

  return (
    <>
      <main className="flex-1 min-w-0">
        <div className="space-y-3 rounded-3xl border border-emerald-100/70 bg-white/90 p-4 sm:p-6 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          {/* Header ligne 1 : titre + bouton ajout */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-zinc-900 dark:text-white truncate">{panelTitle}</h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                {contents.length} {t.knowledges.panel.contentCount}{contents.length > 1 ? 's' : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600"
              data-onboarding="add-content-btn"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">{t.knowledges.contentsPanel.addContentShort}</span>
              <span className="sm:hidden">{t.knowledges.contentsPanel.addContentShort}</span>
            </button>
          </div>
          {/* Header ligne 2 : filtre statut + WP */}
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={e => onStatusFilterChange(e.target.value as ContentStatus | 'all')}
              className="flex-1 min-w-0 rounded-xl border border-zinc-700/50 bg-zinc-800/60 px-3 py-1.5 text-xs text-zinc-200 focus:border-emerald-500/60 focus:outline-none"
            >
              <option value="all">{t.knowledges.contentsPanel.allStatuses}</option>
              <option value="indexed">{t.knowledges.contentsPanel.indexed}</option>
              <option value="transcribed">{t.knowledges.contentsPanel.transcribed}</option>
              <option value="uploaded">{t.knowledges.contentsPanel.pending}</option>
              <option value="transcribing">{t.knowledges.contentsPanel.transcribing}</option>
              <option value="indexing">{t.knowledges.contentsPanel.indexing}</option>
              <option value="error">{t.knowledges.contentsPanel.inError}</option>
            </select>
            <button
              type="button"
              onClick={wpImport.open}
              className="shrink-0 inline-flex items-center justify-center rounded-full border border-blue-900/60 bg-blue-900/30 p-2 text-blue-200 shadow-sm transition hover:bg-blue-900/50"
              title="WordPress"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
              </svg>
            </button>
          </div>

          {/* Liste des contenus */}
          <div className="space-y-3">
            {loading ? (
              <div className="animate-pulse space-y-3 py-4">
                <div className="h-20 rounded-2xl bg-emerald-100/60 dark:bg-emerald-900/40" />
                <div className="h-20 rounded-2xl bg-emerald-100/50 dark:bg-emerald-900/30" />
                <div className="h-20 rounded-2xl bg-emerald-100/40 dark:bg-emerald-900/20" />
              </div>
            ) : contents.length === 0 ? (
              <div className="py-12 text-center">
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center dark:bg-emerald-900/40">
                  <svg className="h-8 w-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-zinc-700 dark:text-zinc-200">{t.knowledges.contentsPanel.emptyTitle}</h3>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                  {selectedCollectionId === 'orphan' 
                    ? t.knowledges.contentsPanel.emptyOrphan
                    : t.knowledges.contentsPanel.emptyDefault}
                </p>
                <button
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  className="mt-4 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600"
                >
                  {t.knowledges.contentsPanel.addFirstContent}
                </button>
              </div>
            ) : (() => {
              const totalPages = Math.ceil(contents.length / PAGE_SIZE)
              const paginated = contents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
              return (
                <>
                  {paginated.map(content => (
                    <ContentCard
                      key={content.id}
                      content={content}
                      isWorking={!!workingIds[content.id]}
                      workingAction={workingIds[content.id]}
                      onViewDetail={() => setDetailContentId(content.id)}
                      onReindex={() => handleReindex(content.id)}
                      onLLMSegment={() => handleLLMSegment(content.id)}
                      onForceTranscribe={() => handleForceTranscribe(content.id)}
                      onDelete={() => setDeleteTarget(content)}
                    />
                  ))}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                      {/* Info */}
                      <span className="text-xs text-zinc-500 dark:text-zinc-400 shrink-0">
                        {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, contents.length)} / {contents.length}
                      </span>

                      {/* Contrôles */}
                      <div className="flex items-center gap-1">
                        {/* Première page */}
                        <button
                          type="button"
                          onClick={() => setPage(1)}
                          disabled={page === 1}
                          className="rounded-lg border border-zinc-200/70 p-1.5 text-zinc-500 transition hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
                          title="Première page"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                          </svg>
                        </button>

                        {/* Page précédente */}
                        <button
                          type="button"
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="rounded-lg border border-zinc-200/70 p-1.5 text-zinc-500 transition hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        {/* Numéros de pages */}
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                          .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                            if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('...')
                            acc.push(p)
                            return acc
                          }, [])
                          .map((item, idx) =>
                            item === '...' ? (
                              <span key={`ellipsis-${idx}`} className="px-1 text-xs text-zinc-400">…</span>
                            ) : (
                              <button
                                key={item}
                                type="button"
                                onClick={() => setPage(item as number)}
                                className={`h-7 min-w-[1.75rem] rounded-lg border px-1.5 text-xs font-semibold transition ${
                                  page === item
                                    ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-400'
                                    : 'border-zinc-200/70 text-zinc-500 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800'
                                }`}
                              >
                                {item}
                              </button>
                            )
                          )
                        }

                        {/* Page suivante */}
                        <button
                          type="button"
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="rounded-lg border border-zinc-200/70 p-1.5 text-zinc-500 transition hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>

                        {/* Dernière page */}
                        <button
                          type="button"
                          onClick={() => setPage(totalPages)}
                          disabled={page === totalPages}
                          className="rounded-lg border border-zinc-200/70 p-1.5 text-zinc-500 transition hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
                          title="Dernière page"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )
            })()}
          </div>
        </div>
      </main>

      {/* Modal Upload */}
      <ContentUploadPanel
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={handleUploadComplete}
        defaultCollectionId={typeof selectedCollectionId === 'number' ? selectedCollectionId : null}
      />

      {/* Modal Détails */}
      <ContentDetailModal
        open={detailContentId !== null}
        contentId={detailContentId}
        collections={collections}
        onClose={() => setDetailContentId(null)}
        onLoadDetail={onGetDetail}
        onDownloadTranscription={onDownloadTranscription}
        onForceTranscribe={async (id) => {
          const success = await onForceTranscribe(id)
          if (success) {
            // Refresh detail
            setDetailContentId(null)
            setTimeout(() => setDetailContentId(id), 100)
          }
          return success
        }}
        onReindex={async (id) => {
          const success = await onReindex(id)
          if (success) {
            setDetailContentId(null)
            setTimeout(() => setDetailContentId(id), 100)
          }
          return success
        }}
        onLLMSegment={async (id) => {
          const success = await onReindex(id, 'llm_segmentation')
          if (success) {
            setDetailContentId(null)
            setTimeout(() => setDetailContentId(id), 100)
          }
          return success
        }}
        onUpdateCollection={onUpdateCollection}
        isWorking={detailContentId !== null && !!workingIds[detailContentId]}
      />

      {/* Modal Suppression */}
      <DeleteConfirmModal
        open={deleteTarget !== null}
        title={t.knowledges.contentsPanel.deleteContentTitle}
        itemName={deleteTarget?.title || ''}
        itemId={deleteTarget?.id || 0}
        warningMessage={t.knowledges.contentsPanel.deleteContentWarning}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />

      {/* WordPress Import Drawer */}
      <WordPressImportDrawer
        isOpen={wpImport.isOpen}
        step={wpImport.state.step}
        selectedType={wpImport.state.selectedType}
        selectedItems={wpImport.state.selectedItems}
        contentTypes={wpImport.contentTypes}
        contents={wpImport.contents}
        collections={collections}
        quota={wpImport.quota}
        estimate={wpImport.estimate}
        importStatus={wpImport.importStatus}
        total={wpImport.total}
        hasMore={wpImport.hasMore}
        loading={wpImport.loading}
        loadingContents={wpImport.loadingContents}
        error={wpImport.state.error}
        onClose={wpImport.close}
        onSelectType={wpImport.selectType}
        onToggleItem={wpImport.toggleItem}
        onSelectAll={wpImport.selectAll}
        onSelectNone={wpImport.selectNone}
        onLoadMore={wpImport.loadMore}
        onConfirm={wpImport.goToConfirmation}
        onBackToSelection={wpImport.backToSelection}
        onBackToTypes={wpImport.backToTypes}
        onStartImport={wpImport.startImport}
        onCancelImport={wpImport.cancelImport}
        targetCollectionId={wpImport.state.targetCollectionId}
        autoCreateCollections={wpImport.state.autoCreateCollections}
        onSetTargetCollection={wpImport.setTargetCollection}
        onSetAutoCreateCollections={wpImport.setAutoCreateCollections}
      />
    </>
  )
}
